import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchFiles } from '../contexts/SearchFilesContext';
import Content from '../components/Content';
import AdvancedSearchPopup from '../components/AdvancedSearchPopup';
import SearchInterface from '../components/SearchInterface';
import SearchSummary from '../components/SearchSummary';

function HomePage() {
  const { getSelectedFiles, updateSelectedFiles, homeSearchState, setHomeSearchState, searchCount, setSearchCount } = useSearchFiles();
  const [allFileNames, setAllFileNames] = useState([]);
  const [activeLawFiles, setActiveLawFiles] = useState(getSelectedFiles('homepage'));
  const [isLoading, setIsLoading] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  // Map context state
  const searchQuery = homeSearchState.query;
  const submittedQuery = homeSearchState.submittedQuery;
  const fileResults = homeSearchState.results;
  const searchAttempted = homeSearchState.attempted;
  const data = homeSearchState.data;

  const setSearchQuery = (val) => setHomeSearchState(prev => ({ ...prev, query: val }));
  const setSubmittedQuery = (val) => setHomeSearchState(prev => ({ ...prev, submittedQuery: val }));
  const setFileResults = (val) => setHomeSearchState(prev => ({ ...prev, results: val }));
  const setSearchAttempted = (val) => setHomeSearchState(prev => ({ ...prev, attempted: val }));
  const setData = (val) => setHomeSearchState(prev => ({ ...prev, data: val }));
  const setScrollPosition = (val) => setHomeSearchState(prev => ({ ...prev, scrollPosition: val }));
  
  const scrollRef = useRef(0);

  // Restore scroll position when results are rendered
  useEffect(() => {
    // Only restore if we have results and a saved position
    if (homeSearchState.scrollPosition > 0 && homeSearchState.results.length > 0) {
      // Use a slightly longer timeout to ensure DOM is painted
      const timer = setTimeout(() => {
        window.scrollTo(0, homeSearchState.scrollPosition);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [homeSearchState.results]);

  // Track scroll using useLayoutEffect to capture position right before unmount/paint changes
  React.useLayoutEffect(() => {
    const handleScroll = () => {
      scrollRef.current = window.scrollY;
    };
    
    // Initialize
    scrollRef.current = window.scrollY;

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Save valid scroll position (ignore 0 if we had a value before, to prevent reset on fast unmounts)
      if (scrollRef.current > 0) {
        setScrollPosition(scrollRef.current);
      }
    };
  }, []);

  const handleSearch = () => {
    const normalizedQuery = searchQuery.trim();
    setSearchAttempted(true);
    setSubmittedQuery(normalizedQuery);
    setSearchCount(prevCount => prevCount + 1);
  };

  const handleApplyAdvancedSearch = (selectedFiles) => {
    console.log("HomePage: Received selected files from popup:", Array.from(selectedFiles));
    setActiveLawFiles(selectedFiles);
    updateSelectedFiles(selectedFiles, 'homepage');
    setData([]); // Clear data to force reload with new files
    setShowAdvancedSearch(false);
  };

  const fetchLawFiles = useCallback(async () => {
    try {
      const response = await fetch(`/laws/manifest.json?v=${new Date().getTime()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch law manifest');
      }
      const fileList = await response.json();
      setAllFileNames(fileList);
      // CRITICAL FIX: Do NOT reset activeLawFiles here.
      // Removed: setActiveLawFiles(new Set(['CivilCode.txt', 'CivilPro.txt']));
    } catch (error) {
      console.error("Could not fetch law files:", error);
    }
  }, []);

  useEffect(() => {
    fetchLawFiles();
  }, [fetchLawFiles]);

  const handleRefreshRequest = () => {
    fetchLawFiles();
  };


  const normalizeText = (text) => {
    // Remove zero-width spaces that might be inserted by iOS Khmer keyboards
    let normalized = text.replace(/\u200B/g, '');
    // Normalize by finding "មាត្រា" followed by one or more spaces and a digit (Arabic or Khmer),
    // and removing the spaces.
    // Arabic digits: 0-9
    // Khmer digits: \u17E0-\u17E9
    return normalized.toLowerCase().replace(/មាត្រា\s+([0-9\u17E0-\u17E9])/g, 'មាត្រា$1');
  };

  useEffect(() => {
    if (!searchAttempted || !submittedQuery) {
      setFileResults([]);
      return;
    }

    // Split query by comma, normalize each term, and filter out empty strings
    const searchTerms = submittedQuery.split(',')
      .map(term => normalizeText(term.trim()))
      .filter(term => term.length > 0);

    // If no valid search terms (e.g. just commas), return
    if (searchTerms.length === 0) {
       setFileResults([]);
       return;
    }

    const results = data.filter(item => {
      const normalizedItemName = normalizeText(item.name);
      // Returns true if the item contains ANY of the search terms
      return searchTerms.some(term => normalizedItemName.includes(term));
    });
    
    setFileResults(results);
  }, [data, submittedQuery, searchAttempted]);

  useEffect(() => {
    const loadFileContent = async () => {
      setIsLoading(true);
      const fileData = [];
      for (const fileName of activeLawFiles) {
        try {
          const response = await fetch(`/laws/${fileName}?v=${new Date().getTime()}`);
          const arrayBuffer = await response.arrayBuffer();
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(arrayBuffer);
          const segments = text.split(/(?=<)/);
          segments.forEach((segment, index) => {
            if (segment.trim()) {
              fileData.push({
                id: `${fileName}-segment-${index}`,
                name: segment,
                source: fileName,
              });
            }
          });
        } catch (error) {
          console.error(`Failed to load content for ${fileName}:`, error);
        }
      }
      setData(fileData);
      setIsLoading(false);
    };

    if (activeLawFiles.size > 0) {
      loadFileContent();
    } else {
      if (data.length > 0) setData([]);
    }
  }, [activeLawFiles]);

  return (
    <>
      <SearchInterface
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onToggleAdvancedSearch={() => setShowAdvancedSearch(!showAdvancedSearch)}
        isLoading={isLoading}
      />
      <SearchSummary
        searchAttempted={searchAttempted}
        searchQuery={submittedQuery}
        fileResultsCount={fileResults ? fileResults.length : 0}
      />
      {showAdvancedSearch && (
        <AdvancedSearchPopup
          key={`advanced-search-popup-homepage-${Date.now()}`} // Force remount with new key
          allFileNames={allFileNames}
          initiallyActiveFiles={activeLawFiles}
          onApply={handleApplyAdvancedSearch}
          onClose={() => setShowAdvancedSearch(false)}
          onRefreshRequest={handleRefreshRequest}
          context="homepage"
        />
      )}
      <Content
        fileResults={fileResults}
        isLoading={isLoading}
        searchAttempted={searchAttempted}
        searchQuery={submittedQuery}
      />
    </>
  );
}

export default HomePage;

